import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * The company's things in people's hands (ทะเบียนทรัพย์สิน): an asset is lent to a person on a handover, which they
 * confirm themselves, and comes back on a return with its condition; damaged or lost, accounting decides whether it
 * comes out of their security deposit. Uniforms and the like are given, not lent: only noted. A branch lead looks
 * after their branch's, HR every branch's.
 */

export const assetKinds = ['phone', 'scanner', 'computer', 'tool', 'other'] as const
/**
 * `atBranch`: kept at its branch (in use there, or free to lend); `lending`: on a handover its person has not confirmed;
 * `withPerson`: in someone's hands; `repair`; `lost`; `retired`: no longer used. The last four but `repair` are set by
 * handovers; staff set `atBranch`, `repair` and `retired` on one that is not lent
 */
export const assetStatuses = ['atBranch', 'lending', 'withPerson', 'repair', 'lost', 'retired'] as const
export type AssetStatus = (typeof assetStatuses)[number]
/** What staff may set by hand, on an asset nobody holds */
export const assetStatusesByHand: readonly AssetStatus[] = ['atBranch', 'repair', 'retired']

/**
 * company.asset (ทรัพย์สิน): one thing the company owns, by its number (`AS6910-0001` when left empty), with the branch
 * it belongs to and what it is worth (what a loss costs). Who holds it and since when come from its handovers.
 */
export const asset = defineResource({
  app: 'company',
  name: 'asset',
  path: 'assets',
  schema: z.object({
    code: z.string().trim().max(30).nullish(),
    name: z.string().trim().min(1).max(200),
    kind: z.enum(assetKinds).default('other'),
    serialNo: z.string().trim().max(100).nullish(),
    branchId: z.string().min(1),
    value: z.number().min(0).nullish(),
    acquiredOn: z.iso.date().nullish(),
    status: z.enum(assetStatuses).default('atBranch'),
    photoFileId: z.string().nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 140, priority: 1 }, form: { span: 4 }, copy: false },
    name: { search: true, list: { priority: 1 }, form: { span: 8 } },
    /** Its number and name, as pickers and other documents show it: two phones of one model tell apart */
    label: { readOnly: true, search: true, list: false, export: false },
    kind: {
      filter: true,
      tones: { phone: 'info', scanner: 'info', computer: 'progress', tool: 'neutral', other: 'neutral' },
      list: { width: 130, priority: 1 },
      form: { span: 4 },
    },
    serialNo: { search: true, list: { priority: 3 }, form: { span: 4 }, copy: false },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 2 },
      form: { span: 4 },
    },
    value: { type: 'money', list: { width: 130, align: 'end', priority: 3 }, form: { span: 4 } },
    acquiredOn: { type: 'date', list: { priority: 3, hidden: true }, form: { span: 4 } },
    status: {
      filter: true,
      tones: { atBranch: 'success', lending: 'warning', withPerson: 'info', repair: 'warning', lost: 'danger', retired: 'neutral' },
      list: { width: 140, priority: 1 },
      form: { span: 4, custom: true },
      copy: false,
    },
    /** Who has it now (a handover's person, once confirmed) */
    holderUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 } },
    heldSince: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    photoFileId: { type: 'file', list: false, form: { span: 12 }, import: false, export: false },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'label',
  columns: ['code', 'name', 'kind', 'status', 'holderUserId', 'branchId', 'serialNo', 'value', 'acquiredOn'],
  defaultSort: 'code',
  actions: ['read', 'create', 'update', 'delete', 'export', 'import'],
  ownerField: 'holderUserId',
  mineFields: ['holderUserId'],
  branchFields: ['branchId'],
  // Several of one model bought together: each a copy of the first, with its own serial number
  duplicate: true,
  card: { title: 'name', subtitle: 'code', meta: ['holderUserId', 'branchId'], status: 'status' },
})

/** `lend`: assets go to a person; `return`: they come back */
export const handoverKinds = ['lend', 'return'] as const
export const handoverStatuses = ['draft', 'sent', 'received', 'returned', 'cancelled'] as const
/** How an asset came back: `normal` (back at its branch), `damaged` (to repair), `lost` */
export const returnConditions = ['normal', 'damaged', 'lost'] as const
export type ReturnCondition = (typeof returnConditions)[number]
/** Of a return with something damaged or lost: `pending` for accounting, then `deducted` (security deposit) or `waived` */
export const handoverReviewStatuses = ['none', 'pending', 'deducted', 'waived'] as const

/** One asset of a return and how it came back */
export const returnLine = z.object({
  assetId: z.string().min(1),
  condition: z.enum(returnConditions).default('normal'),
  note: z.string().trim().max(300).nullish(),
})
export type ReturnLine = z.infer<typeof returnLine>

/** What a return costs: the value of what came back damaged or lost (an asset with no value counts 0) */
export function returnLoss(lines: readonly ReturnLine[], worth: (assetId: string) => number) {
  return Math.round(lines.filter((line) => line.condition !== 'normal').reduce((sum, line) => sum + worth(line.assetId), 0) * 100) / 100
}

/**
 * company.assetHandover (ใบส่งมอบ / รับคืนทรัพย์สิน): assets of one branch lent to a person, or coming back from them.
 * A lend sent (`HO6910-0001`) waits for the person to confirm in the app (`receive`), or they sign for it on the giver's
 * screen (`signatureFileId`) and have it at once. A return (`RT…`) takes the assets back at once, each `normal`,
 * `damaged` or `lost`; damaged or lost ones wait for accounting (`review`): taken from the person's security deposit
 * (cash.bond `damage`), or waived — with the reason.
 */
export const assetHandover = defineResource({
  app: 'company',
  name: 'assetHandover',
  path: 'asset-handovers',
  schema: z.object({
    kind: z.enum(handoverKinds).default('lend'),
    userId: z.string().min(1),
    assetIds: z.array(z.string().min(1)).min(1).max(50),
    /** A return: how each asset came back (one line per asset) */
    lines: z.array(returnLine).max(50).default([]),
    /** A lend signed for on the giver's screen, by someone without a phone */
    signatureFileId: z.string().nullish(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    kind: { filter: true, tones: { lend: 'info', return: 'neutral' }, list: { width: 120, priority: 1 }, form: { span: 4, custom: true } },
    userId: { type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 }, form: { span: 8, custom: true } },
    assetIds: { type: 'multiselect', ref: { resource: 'company.asset' }, list: { priority: 2 }, form: { span: 12, custom: true } },
    lines: { type: 'json', list: false, form: { span: 12, custom: true }, import: false, export: false },
    signatureFileId: { type: 'file', list: false, form: { span: 12, custom: true }, import: false, export: false },
    status: {
      readOnly: true,
      type: 'select',
      options: handoverStatuses,
      filter: true,
      tones: { draft: 'neutral', sent: 'warning', received: 'success', returned: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    sentAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    receivedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    /** The value of what came back damaged or lost */
    loss: { readOnly: true, type: 'money', list: { width: 120, align: 'end', priority: 3 } },
    reviewStatus: {
      readOnly: true,
      type: 'select',
      options: handoverReviewStatuses,
      filter: true,
      tones: { none: 'neutral', pending: 'warning', deducted: 'danger', waived: 'success' },
      list: { width: 150, priority: 2 },
    },
    reviewNote: { readOnly: true, list: false },
    reviewedBy: { readOnly: true, list: false },
    reviewedAt: { readOnly: true, type: 'datetime', list: false },
    /** The security deposit slip a deduction was taken on */
    bondId: { readOnly: true, type: 'ref', ref: { resource: 'cash.bond' }, list: false },
    recordedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 3, hidden: true } },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'number',
  columns: ['number', 'sentAt', 'kind', 'userId', 'assetIds', 'status', 'reviewStatus', 'loss', 'branchId', 'recordedByUserId'],
  defaultSort: '-sentAt',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'receive', 'review'],
  ownerField: ['userId', 'recordedByUserId'],
  mineFields: ['userId'],
  branchFields: ['branchId'],
  submittable: { allowOnSubmit: ['notes'], cancelReason: 'required' },
  card: { title: 'userId', subtitle: 'number', meta: ['sentAt', 'assetIds'], status: 'status' },
})

/**
 * company.uniform (ยูนิฟอร์มและของที่แจก): something given to a person to keep — a shirt, a jacket, safety shoes — noted
 * with its size and how many, at their branch. Not lent: nothing comes back.
 */
export const uniform = defineResource({
  app: 'company',
  name: 'uniform',
  path: 'uniforms',
  schema: z.object({
    userId: z.string().min(1),
    issuedOn: z.iso.date(),
    item: z.string().trim().min(1).max(100),
    size: z.string().trim().max(20).nullish(),
    quantity: z.number().int().min(1).max(100).default(1),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    userId: { type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 }, form: { span: 6, custom: true } },
    issuedOn: { type: 'date', filter: true, list: { width: 130, priority: 1 }, form: { span: 6 } },
    item: { search: true, suggest: true, list: { priority: 1 }, form: { span: 6 } },
    size: { suggest: true, list: { width: 90, priority: 2 }, form: { span: 3 } },
    quantity: { type: 'integer', list: { width: 90, align: 'end', priority: 2 }, form: { span: 3 } },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3 } },
    recordedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'item',
  columns: ['issuedOn', 'userId', 'item', 'size', 'quantity', 'branchId'],
  defaultSort: '-issuedOn',
  actions: ['read', 'create', 'update', 'delete', 'export'],
  ownerField: 'userId',
  mineFields: ['userId'],
  branchFields: ['branchId'],
  card: { title: 'item', subtitle: 'userId', meta: ['issuedOn', 'size', 'quantity'] },
})
